/* DashboardTooltip — hover-card didático para KPIs do Dashboard.
 * Abre no hover ou focus, fecha no ESC, funciona em mobile por toque.
 * Não interfere no clique de drilldown do card.
 */
import { HelpCircle } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { getKpiExplanation } from "@/lib/dashboardExplanations";
import { api } from "@/lib/api";

// Throttle in-session: cada KPI é auditado no máximo 1x por página
const openedKPIs = new Set();

function logHelpOpened(label) {
  if (openedKPIs.has(label)) return;
  openedKPIs.add(label);
  api.post("/config/kpi-help-event", { kpi: label }).catch(() => {
    // fire-and-forget; se falhar, remove do set para tentar de novo
    openedKPIs.delete(label);
  });
}

export default function DashboardTooltip({ label, side = "top", align = "end" }) {
  const info = getKpiExplanation(label);
  if (!info) return null;
  return (
    <HoverCard
      openDelay={120}
      closeDelay={80}
      onOpenChange={(open) => { if (open) logHelpOpened(label); }}
    >
      <HoverCardTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.stopPropagation(); }}
          aria-label={`Ajuda: ${label}`}
          className="inline-flex items-center rounded-full p-1 text-[var(--text-muted)] hover:text-[var(--gold)] focus:outline-none focus:text-[var(--gold)] focus:ring-2 focus:ring-amber-400/40 transition cursor-help"
          data-testid={`dashboard-help-${label.toLowerCase().replace(/[\s/]+/g, "-")}`}
        >
          <HelpCircle size={13} />
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side={side}
        align={align}
        sideOffset={6}
        className="w-72 z-50 p-0 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3.5 space-y-2 text-[13px]">
          <div className="font-display font-semibold text-[var(--text)] text-sm">
            {info.titulo || label}
          </div>
          <p className="text-[var(--text)] leading-snug">{info.explicacao}</p>
          {info.origem && (
            <div className="pt-1.5 border-t border-[var(--border)]">
              <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)] mb-0.5">
                Origem do dado
              </div>
              <code className="text-[11px] font-mono text-[var(--gold)] break-all">{info.origem}</code>
            </div>
          )}
          {info.dica && (
            <div className="pt-1.5 border-t border-[var(--border)]">
              <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)] mb-0.5">
                Dica de uso
              </div>
              <p className="text-[12px] leading-snug">{info.dica}</p>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
